// ─────────────────────────────────────────────────────────────────────────────
// OnsitePM Shared PDF Design System — @react-pdf/renderer Helpers
// ─────────────────────────────────────────────────────────────────────────────
// All components use @react-pdf/renderer primitives.
// Styles are memoized via StyleSheet.create().
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { COLORS, FONTS, SPACING } from './tokens';

// ─────────────────────────────────────────────────────────────────────────────
// Shared StyleSheet (memoized)
// ─────────────────────────────────────────────────────────────────────────────
const styles = StyleSheet?.create({
  // ── Header ────────────────────────────────────────────────────────────────
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 0,
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'column',
    paddingRight: 16,
  },
  headerLogo: {
    maxWidth: 180,
    maxHeight: 50,
    objectFit: 'contain',
    objectPositionX: 'left',
    marginBottom: 4,
  },
  headerTagline: {
    fontSize: 7.5,
    fontFamily: 'Helvetica-Oblique',
    color: COLORS?.gray500,
    marginBottom: 2,
  },
  headerAddress: {
    fontSize: 7.5,
    color: COLORS?.gray500,
    marginBottom: 2,
  },
  headerContact: {
    fontSize: 7.5,
    color: COLORS?.gray500,
  },
  headerTaxInfo: {
    fontSize: 6.5,
    color: COLORS?.zinc400,
    textAlign: 'left',
    marginTop: 2,
  },
  headerRight: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: FONTS?.title?.size,
    color: COLORS?.black,
    letterSpacing: FONTS?.title?.letterSpacing,
    textAlign: 'right',
    marginBottom: 2,
  },
  headerSubtitle: {
    fontSize: FONTS?.subtitle?.size,
    color: COLORS?.gray500,
    letterSpacing: 3,
    textAlign: 'right',
    marginBottom: 8,
  },
  headerMetaContainer: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginBottom: 6,
  },
  headerMetaRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 3,
  },
  headerMetaLabel: {
    fontSize: 8,
    color: COLORS?.gray500,
    width: 60,
    textAlign: 'right',
    marginRight: 6,
  },
  headerMetaValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8.5,
    color: COLORS?.black,
    minWidth: 60,
    textAlign: 'right',
  },
  headerDivider: {
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS?.hairline,
    marginTop: 12,
    marginBottom: 14,
  },

  // ── Footer ────────────────────────────────────────────────────────────────
  footerContainer: {
    position: 'absolute',
    bottom: 10,
    left: SPACING?.pageMarginPt,
    right: SPACING?.pageMarginPt,
  },
  footerRule: {
    borderTopWidth: 0.5,
    borderTopColor: COLORS?.gray300,
    paddingTop: 6,
  },
  footerInner: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerTextLeft: {
    fontSize: 7,
    color: COLORS?.gray500,
  },
  footerTextRight: {
    fontSize: 7,
    color: COLORS?.gray500,
  },

  // ── Section Title ─────────────────────────────────────────────────────────
  sectionTitleContainer: {
    marginBottom: 5,
    marginTop: 8,
  },
  sectionTitleText: {
    fontSize: FONTS?.sectionLabel?.size,
    fontFamily: 'Helvetica-Bold',
    color: COLORS?.black,
    letterSpacing: FONTS?.sectionLabel?.letterSpacing,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  sectionTitleUnderline: {
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS?.gray300,
    marginTop: 3,
    marginBottom: 8,
  },

  // ── Meta Row ──────────────────────────────────────────────────────────────
  metaRowContainer: {
    flexDirection: 'row',
    paddingVertical: 4,
  },
  metaRowContainerBordered: {
    flexDirection: 'row',
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomStyle: 'dotted',
    borderBottomColor: COLORS?.gray300,
  },
  metaRowLabel: {
    flex: 1,
    fontSize: 8,
    color: COLORS?.gray500,
    letterSpacing: 0.3,
  },
  metaRowValue: {
    flex: 1.5,
    fontSize: 8.5,
    color: COLORS?.black,
  },

  // ── Info Columns ──────────────────────────────────────────────────────────
  infoColumnsRow: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 8,
  },
  infoColumn: {
    flex: 1,
  },

  // ── Professionals List ────────────────────────────────────────────────────
  profBlock: {
    marginBottom: 6,
    paddingBottom: 6,
    borderBottomWidth: 0.5,
    borderBottomStyle: 'dotted',
    borderBottomColor: COLORS?.gray300,
  },
  profBlockLast: {
    marginBottom: 0,
    paddingBottom: 0,
  },
  profRole: {
    fontSize: 6.5,
    color: COLORS?.gray500,
    letterSpacing: 0.5,
    marginBottom: 1,
  },
  profName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 8,
    color: COLORS?.black,
    marginBottom: 1,
  },
  profInfo: {
    fontSize: 7.5,
    color: COLORS?.gray500,
  },

  // ── Approval Section ──────────────────────────────────────────────────────
  approvalSection: {
    marginTop: 20,
    marginBottom: 16,
  },
  approvalGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 48,
  },
  approvalCard: {
    width: '44%',
    border: '0.5pt solid #e4e4e7',
    borderRadius: 4,
    padding: 12,
    marginBottom: 12,
  },
  approvalCardAccent: {
    width: '44%',
    border: '0.5pt solid #e4e4e7',
    borderTopWidth: 2,
    borderTopColor: COLORS?.black,
    borderRadius: 4,
    padding: 12,
    marginBottom: 12,
  },
  approvalCardRole: {
    fontSize: 6.5,
    color: COLORS?.gray500,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  approvalCardRoleAccent: {
    fontSize: 6.5,
    color: COLORS?.black,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  approvalCardName: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS?.black,
    marginBottom: 2,
  },
  approvalCardInfo: {
    fontSize: 7.5,
    color: COLORS?.gray500,
    marginBottom: 42,
  },
  approvalSignatureLine: {
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS?.gray400,
    marginBottom: 3,
    marginTop: 0,
    height: 18,
  },
  approvalSignatureLabel: {
    fontSize: 7,
    color: COLORS?.gray400,
    marginBottom: 6,
  },
  approvalDateLine: {
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS?.gray400,
    marginBottom: 3,
    height: 18,
  },
  approvalDateLabel: {
    fontSize: 7,
    color: COLORS?.gray400,
  },

  // ── Totals Block ──────────────────────────────────────────────────────────
  totalsWrapper: {
    flexDirection: 'row',
    marginTop: 14,
    marginBottom: 20,
  },
  termsBlock: {
    flex: 1,
    paddingRight: 16,
  },
  termsText: {
    fontSize: 7.5,
    color: COLORS?.gray500,
    marginBottom: 4,
    lineHeight: 1.4,
  },
  totalsBlock: {
    flex: 1,
    alignItems: 'flex-end',
  },
  totalRow: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  totalLabel: {
    fontSize: 9,
    color: COLORS?.gray700,
  },
  totalValue: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS?.black,
    textAlign: 'right',
  },
  totalDivider: {
    borderTopWidth: 0.6,
    borderTopColor: COLORS?.black,
    width: '100%',
    marginTop: 4,
    marginBottom: 0,
  },
  totalRowHighlight: {
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
    backgroundColor: COLORS?.orange,
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 16,
    marginTop: 6,
  },
  totalLabelBold: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS?.white,
  },
  totalValueBold: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 9,
    color: COLORS?.white,
    textAlign: 'right',
  },

  // ── Items Table ───────────────────────────────────────────────────────────
  tableParentHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS?.black,
  },
  tableSubHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS?.tableSubHeaderBg,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.4,
    borderBottomColor: COLORS?.gray200,
    backgroundColor: COLORS?.white,
  },
  tableRowAlt: {
    flexDirection: 'row',
    borderBottomWidth: 0.4,
    borderBottomColor: COLORS?.gray200,
    backgroundColor: COLORS?.gray50,
  },
  thCell: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  thText: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 6.8,
    color: COLORS?.offWhite,
    letterSpacing: 0.8,
    textAlign: 'center',
  },
  tdCell: {
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  tdText: {
    fontSize: 8.5,
    color: COLORS?.black,
  },
  tdTextRight: {
    fontSize: 8.5,
    color: COLORS?.black,
    textAlign: 'right',
  },
});

// ─────────────────────────────────────────────────────────────────────────────
// PdfHeader
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {string|null} logoBase64    - base64 data URL for the org logo
 * @param {string}      docTitle      - main document title (right side)
 * @param {string}      [docSubtitle] - subtitle below title
 * @param {Array<{label:string, value:string}>} [meta] - meta rows (right side)
 * @param {string}      [tagline]     - org tagline (left, oblique gray500)
 * @param {string}      [address]     - org address (left, gray500)
 * @param {string}      [contactLine] - org contact line (left, gray500)
 * @param {string}      [taxLine]     - tax/RBQ line (left, zinc400, 6.5pt)
 */
export const PdfHeader = ({
  logoBase64  = null,
  docTitle    = '',
  docSubtitle = '',
  meta        = [],
  tagline     = '',
  address     = '',
  contactLine = '',
  taxLine     = '',
}) => (
  <View>
    <View style={styles?.headerContainer}>
      {/* Left: logo + optional text slots */}
      <View style={styles?.headerLeft}>
        {logoBase64 ? (
          <Image style={styles?.headerLogo} src={logoBase64} />
        ) : null}
        {tagline ? (
          <Text style={styles?.headerTagline}>{tagline}</Text>
        ) : null}
        {address ? (
          <Text style={styles?.headerAddress}>{address}</Text>
        ) : null}
        {contactLine ? (
          <Text style={styles?.headerContact}>{contactLine}</Text>
        ) : null}
        {taxLine ? (
          <Text style={styles?.headerTaxInfo}>{taxLine}</Text>
        ) : null}
      </View>

      {/* Right: title + subtitle + meta */}
      <View style={styles?.headerRight}>
        <Text style={styles?.headerTitle}>{docTitle}</Text>
        {docSubtitle ? (
          <Text style={styles?.headerSubtitle}>{docSubtitle}</Text>
        ) : null}
        {meta?.length > 0 ? (
          <View style={styles?.headerMetaContainer}>
            {meta.map((row, idx) => (
              <View key={idx} style={styles?.headerMetaRow}>
                <Text style={styles?.headerMetaLabel}>{row?.label}</Text>
                <Text style={styles?.headerMetaValue}>{row?.value ?? ''}</Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </View>
    {/* Hairline divider below header */}
    <View style={styles?.headerDivider} />
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// PdfFooter
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {string}   [leftText]    - text rendered on the left side of the footer
 * @param {string}   [rightText]   - static text rendered on the right side (used when renderRight is not provided)
 * @param {function} [renderRight] - optional @react-pdf/renderer render prop function
 *                                   receives { pageNumber, totalPages }; when provided,
 *                                   renders a dynamic <Text render={renderRight} /> on the right.
 *                                   When NOT provided, falls back to static rightText.
 */
export const PdfFooter = ({
  leftText     = '',
  rightText    = '',
  renderRight  = null,
}) => (
  <View style={styles?.footerContainer} fixed>
    <View style={styles?.footerRule}>
      <View style={styles?.footerInner}>
        <Text style={styles?.footerTextLeft}>{leftText}</Text>
        {renderRight ? (
          <Text style={styles?.footerTextRight} render={renderRight} />
        ) : (
          <Text style={styles?.footerTextRight}>{rightText}</Text>
        )}
      </View>
    </View>
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// PdfSectionTitle
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {string} label
 */
export const PdfSectionTitle = ({ label = '' }) => (
  <View style={styles?.sectionTitleContainer}>
    <Text style={styles?.sectionTitleText}>{label}</Text>
    <View style={styles?.sectionTitleUnderline} />
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// PdfMetaRow
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {string}  label
 * @param {string}  value
 * @param {boolean} [hasBorder] - adds dotted bottom border when true
 */
export const PdfMetaRow = ({ label = '', value = '', hasBorder = false }) => (
  <View style={hasBorder ? styles?.metaRowContainerBordered : styles?.metaRowContainer}>
    <Text style={styles?.metaRowLabel}>{label}</Text>
    <Text style={styles?.metaRowValue}>{String(value ?? '')}</Text>
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// PdfInfoColumns
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Three (or N) equal-width columns, each with a section title + dotted meta rows.
 * @param {Array<{title:string, rows:Array<{label:string, value:string}>}>} columns
 */
export const PdfInfoColumns = ({ columns = [] }) => (
  <View style={styles?.infoColumnsRow}>
    {columns?.map((col, colIdx) => (
      <View key={colIdx} style={styles?.infoColumn}>
        <PdfSectionTitle label={col?.title ?? ''} />
        {col?.rows?.map((row, rowIdx) => {
          const isLast = rowIdx === (col?.rows?.length ?? 0) - 1;
          return (
            <PdfMetaRow
              key={rowIdx}
              label={row?.label ?? ''}
              value={row?.value ?? ''}
              hasBorder={!isLast}
            />
          );
        })}
      </View>
    ))}
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// PdfProfessionalsList
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Compact stacked list of professionals (role / name / info).
 * @param {Array<{role:string, name:string, info:string}>} professionals
 */
export const PdfProfessionalsList = ({ professionals = [] }) => (
  <View>
    {professionals?.map((prof, idx) => {
      const isLast = idx === (professionals?.length ?? 0) - 1;
      return (
        <View key={idx} style={isLast ? styles?.profBlockLast : styles?.profBlock}>
          {prof?.role ? (
            <Text style={styles?.profRole}>{prof?.role}</Text>
          ) : null}
          {prof?.name ? (
            <Text style={styles?.profName}>{prof?.name}</Text>
          ) : null}
          {prof?.info ? (
            <Text style={styles?.profInfo}>{prof?.info}</Text>
          ) : null}
        </View>
      );
    })}
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// PdfApprovalSection
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Generic approval grid: renders one card per entry in `cards`.
 * Cards with accent:true get a 2px navy top border + navy role label (client card).
 * Professionals should come first; client card last.
 *
 * @param {string} title
 * @param {Array<{role:string, name:string, info:string, accent?:boolean}>} cards
 * @param {string} [signatureLabel]
 * @param {string} [dateLabel]
 *
 * Legacy props (pmFullName, pmInfo, professionals) are intentionally NOT accepted —
 * the caller (PaymentRequestDocument) builds the cards array and passes it directly.
 */
export const PdfApprovalSection = ({
  title          = '',
  cards          = [],
  signatureLabel = 'Signature',
  dateLabel      = 'Date',
}) => (
  <View style={styles?.approvalSection}>
    <PdfSectionTitle label={title} />
    <View style={styles?.approvalGrid}>
      {cards?.map((card, idx) => (
        <View key={idx} style={card?.accent ? styles?.approvalCardAccent : styles?.approvalCard}>
          {card?.role ? (
            <Text style={card?.accent ? styles?.approvalCardRoleAccent : styles?.approvalCardRole}>
              {card?.role}
            </Text>
          ) : null}
          {card?.name ? (
            <Text style={styles?.approvalCardName}>{card?.name}</Text>
          ) : null}
          {card?.info ? (
            <Text style={styles?.approvalCardInfo}>{card?.info}</Text>
          ) : null}
          <View style={styles?.approvalSignatureLine} />
          <Text style={styles?.approvalSignatureLabel}>{signatureLabel}</Text>
          <View style={styles?.approvalDateLine} />
          <Text style={styles?.approvalDateLabel}>{dateLabel}</Text>
        </View>
      ))}
    </View>
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// PdfTotalsBlock
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Two-column block: terms/notes on the left, totals summary on the right.
 *
 * @param {string[]} [termsLines]  - array of terms/note strings
 * @param {Array<{label:string, value:string, highlight?:boolean, isDivider?:boolean}>} rows
 */
export const PdfTotalsBlock = ({ termsLines = [], rows = [] }) => (
  <View style={styles?.totalsWrapper}>
    {/* Left: terms / notes */}
    <View style={styles?.termsBlock}>
      {termsLines?.map((line, idx) => (
        <Text key={idx} style={styles?.termsText}>{line}</Text>
      ))}
    </View>
    {/* Right: totals rows */}
    <View style={styles?.totalsBlock}>
      {rows?.map((row, idx) => {
        if (row?.isDivider) {
          return <View key={idx} style={styles?.totalDivider} />;
        }
        if (row?.highlight) {
          return (
            <View key={idx} style={styles?.totalRowHighlight}>
              <Text style={styles?.totalLabelBold}>{row?.label ?? ''}</Text>
              <Text style={styles?.totalValueBold}>{row?.value ?? ''}</Text>
            </View>
          );
        }
        return (
          <View key={idx} style={styles?.totalRow}>
            <Text style={styles?.totalLabel}>{row?.label ?? ''}</Text>
            <Text style={styles?.totalValue}>{row?.value ?? ''}</Text>
          </View>
        );
      })}
    </View>
  </View>
);

// ─────────────────────────────────────────────────────────────────────────────
// PdfItemsTable
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Items table with a generic grouped-header mechanism:
 *
 * - `groups` prop (optional): defines the parent header row with colspan-like spanning.
 *   Each group entry is either:
 *     · { label, flex, rowspan: true }  — a non-grouped cell that spans both header rows
 *       (rendered in parent row at full flex; sub-row gets a blank spacer at same flex)
 *     · { label, flex, subCols: [{label, flex}, ...] }  — a grouped cell whose label spans
 *       the combined flex of its sub-columns in the parent row; sub-row renders each subCol.
 *
 * - When `groups` is provided, the parent header row and sub-header row are derived from it.
 * - When `groups` is NOT provided, falls back to the original `columns`-based rendering
 *   (backward-compatible: parentLabel / subLabel per column).
 *
 * DP reference column layout (groups):
 *   col1: "DESCRIPTION DÉTAILS · WORK DESC"  flex:3   rowspan *   col2:"VALEUR À EXÉCUTER ($)"flex:1.5 rowspan *   col3:"À CE JOUR · TO DATE"               flex:2.2 spans 2 subCols (%, $) each flex:1
 *   col4: "DERNIÈRE DEMANDE · LAST REQUEST"   flex:2.2 spans 2 subCols (%, $) each flex:1
 *   col5: "PRÉSENTE DEMANDE ($)"              flex:1.5 rowspan
 *
 * @param {Array<object>} items    - data rows
 * @param {Array<{
 *   key: string,
 *   label: string,
 *   flex: number,
 *   align?: 'left'|'center'|'right',
 *   parentLabel?: string,
 *   subLabel?: string,
 *   renderCell?: (item, idx) => React.Node
 * }>} columns                    - flat column definitions (used for data rows + fallback headers)
 * @param {Array<{
 *   label: string,
 *   flex: number,
 *   rowspan?: boolean,
 *   subCols?: Array<{label: string, flex: number}>
 * }>} [groups]                   - optional grouped-header definitions (see above)
 * @param {boolean} [isExtras]    - when true, renders extras-style layout
 * @param {{ label: string, cells: Array<string|null> }} [totalRow]
 *   Optional total row rendered after the last data row. When absent the
 *   table renders exactly as before. When present: a hairline rule then one
 *   bold row using the same column widths and alignment as the data rows.
 */
export const PdfItemsTable = ({ items = [], columns = [], groups = null, isExtras = false, totalRow }) => {
  // ── Grouped-header path (when groups prop is provided) ──────────────────
  if (groups && groups?.length > 0) {
    return (
      <View>
        {/* Parent header row */}
        <View style={styles?.tableParentHeader}>
          {groups?.map((group, idx) => {
            // For grouped cells, flex = sum of subCols flex (or explicit group.flex)
            const groupFlex = group?.flex;
            return (
              <View key={idx} style={[styles?.thCell, { flex: groupFlex }]}>
                <Text style={styles?.thText}>{group?.label ?? ''}</Text>
              </View>
            );
          })}
        </View>
        {/* Sub-header row */}
        <View style={styles?.tableSubHeader}>
          {groups?.map((group, gIdx) => {
            if (group?.rowspan) {
              // Rowspan cell: blank spacer in sub-row at same flex
              return (
                <View key={gIdx} style={[styles?.thCell, { flex: group?.flex }]}>
                  <Text style={styles?.thText}> </Text>
                </View>
              );
            }
            // Grouped cell: render each subCol
            return (group?.subCols ?? [])?.map((sub, sIdx) => (
              <View key={`${gIdx}-${sIdx}`} style={[styles?.thCell, { flex: sub?.flex }]}>
                <Text style={styles?.thText}>{sub?.label ?? ''}</Text>
              </View>
            ));
          })}
        </View>
        {/* Data rows */}
        {items?.map((item, rowIdx) => {
          const isAlt = rowIdx % 2 !== 0;
          return (
            <View key={rowIdx} style={isAlt ? styles?.tableRowAlt : styles?.tableRow}>
              {columns?.map((col, colIdx) => {
                const isRight = col?.align === 'right';
                const cellContent = col?.renderCell
                  ? col?.renderCell(item, rowIdx)
                  : String(item?.[col?.key] ?? '');
                return (
                  <View key={colIdx} style={[styles?.tdCell, { flex: col?.flex ?? 1 }]}>
                    {typeof cellContent === 'string' ? (
                      <Text style={isRight ? styles?.tdTextRight : styles?.tdText}>
                        {cellContent}
                      </Text>
                    ) : (
                      cellContent
                    )}
                  </View>
                );
              })}
            </View>
          );
        })}
        {/* Optional total row — hairline rule then one bold row */}
        {totalRow ? (
          <>
            <View style={styles?.totalDivider} />
            <View style={styles?.tableRow}>
              {columns?.map((col, colIdx) => {
                const isRight = col?.align === 'right';
                const cellVal = colIdx === 0
                  ? (totalRow?.label ?? '')
                  : (totalRow?.cells?.[colIdx - 1] ?? '');
                return (
                  <View key={colIdx} style={[styles?.tdCell, { flex: col?.flex ?? 1 }]}>
                    <Text style={[
                      isRight ? styles?.tdTextRight : styles?.tdText,
                      { fontFamily: 'Helvetica-Bold' },
                    ]}>
                      {cellVal}
                    </Text>
                  </View>
                );
              })}
            </View>
          </>
        ) : null}
      </View>
    );
  }

  // ── Legacy / flat-column path (backward-compatible) ─────────────────────
  const hasParentRow = columns?.some(col => col?.parentLabel != null);
  const hasSubRow    = columns?.some(col => col?.subLabel != null);

  return (
    <View>
      {/* Parent header row */}
      {hasParentRow ? (
        <View style={styles?.tableParentHeader}>
          {columns?.map((col, idx) => (
            <View key={idx} style={[styles?.thCell, { flex: col?.flex ?? 1 }]}>
              <Text style={styles?.thText}>{col?.parentLabel ?? ''}</Text>
            </View>
          ))}
        </View>
      ) : null}
      {/* Sub-header row */}
      {hasSubRow ? (
        <View style={styles?.tableSubHeader}>
          {columns?.map((col, idx) => (
            <View key={idx} style={[styles?.thCell, { flex: col?.flex ?? 1 }]}>
              <Text style={styles?.thText}>{col?.subLabel ?? col?.label ?? ''}</Text>
            </View>
          ))}
        </View>
      ) : null}
      {/* If no parent/sub rows, render a single header row */}
      {!hasParentRow && !hasSubRow ? (
        <View style={styles?.tableParentHeader}>
          {columns?.map((col, idx) => (
            <View key={idx} style={[styles?.thCell, { flex: col?.flex ?? 1 }]}>
              <Text style={styles?.thText}>{col?.label ?? ''}</Text>
            </View>
          ))}
        </View>
      ) : null}
      {/* Data rows */}
      {items?.map((item, rowIdx) => {
        const isAlt = rowIdx % 2 !== 0;
        return (
          <View key={rowIdx} style={isAlt ? styles?.tableRowAlt : styles?.tableRow}>
            {columns?.map((col, colIdx) => {
              const isRight = col?.align === 'right';
              const cellContent = col?.renderCell
                ? col?.renderCell(item, rowIdx)
                : String(item?.[col?.key] ?? '');
              return (
                <View key={colIdx} style={[styles?.tdCell, { flex: col?.flex ?? 1 }]}>
                  {typeof cellContent === 'string' ? (
                    <Text style={isRight ? styles?.tdTextRight : styles?.tdText}>
                      {cellContent}
                    </Text>
                  ) : (
                    cellContent
                  )}
                </View>
              );
            })}
          </View>
        );
      })}
    </View>
  );
};
